import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { getStoreState } from '../state/Store';
import { generateClassName } from '../utils';
import ButtonTooltip from './components/ButtonTooltip';

import PerfectScrollbar from 'react-perfect-scrollbar';
import Layer from '../data/Layer';
import { Effect } from '@beatflyer/shaders-lib';
import { CompositionEffect } from 'compose/CompositionEffect';
import { FrameType } from 'data/FrameType';

(window as any).BeatFlyer.compositions = {};
require('../compose/ComposeFade');
require('../compose/ComposeZoom');
require('../compose/ComposeSlide');
require('../compose/ComposeScroll');
require('../compose/ComposeSpin');
require('../compose/ComposeNeon');
require('../compose/ComposeText');

interface EffectRowProps {
  id: string;
  newLabel: string | null;
  selected: boolean;
  isEditing: boolean;
  onClick: Function;
  onShowSettings: Function;
}

interface EffectRowState {
  isOver: boolean;
}

class EffectRow extends React.Component<EffectRowProps, EffectRowState> {
  state = {
    isOver: false
  };

  // didClickInvert = () => {
  //   storeDispatch(
  //     Actions.toggleFilterDirection(null, getStoreState().flyer.previewedEffect)
  //   );
  // };

  didClickSettings = (event: React.MouseEvent<HTMLElement>) => {
    this.props.onShowSettings(
      event.currentTarget,
      getStoreState().flyer.previewedEffect
    );
  };

  didClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!this.props.selected) {
      this.props.onClick(this.props.id);
    }
  };

  onMouseOver = () => {
    this.setState({ isOver: true });
  };

  onMouseOut = () => {
    this.setState({ isOver: false });
  };

  render() {
    const asset = this.state.isOver
      ? `animated/${this.props.id}.gif`
      : `${this.props.id}.png`;
    const isComposition = this.props.id.startsWith('composition-');

    const filter = (window as any).BeatFlyer[
      isComposition ? 'compositions' : 'filters'
    ][this.props.id];
    const previewedEffect = getStoreState().flyer.previewedEffect;

    return (
      <div
        className={generateClassName('effect-row', {
          selected: this.props.selected,
          editing: this.props.isEditing
        })}
        onMouseOver={this.onMouseOver}
        onMouseOut={this.onMouseOut}
        onClick={this.didClick}
      >
        <img alt="" src={`/media/filters-thumbs/${asset}`} draggable={false} />
        <span>{this.props.newLabel || filter.label}</span>
        {this.props.selected && (
          <div
            className={generateClassName('buttons', {
              hidden: previewedEffect && !previewedEffect.visible,
              inverted:
                previewedEffect &&
                !(previewedEffect instanceof CompositionEffect) &&
                previewedEffect.isInverted
            })}
          >
            {/* <ButtonTooltip text="Invert signal">
              <button className="invert" onClick={this.didClickInvert} />
            </ButtonTooltip> */}
            <ButtonTooltip text="settings">
              <button className="settings" onClick={this.didClickSettings} />
            </ButtonTooltip>
          </div>
        )}
      </div>
    );
  }
}

interface EffectGroupProps {
  title: string;
  items: (string | Item)[];
  onSelect: (id: string) => void;
  selectedEffect: string | null;
  isEditing: boolean;
  onShowSettings: (target: HTMLElement, effect: Effect) => void;
}

class EffectGroup extends React.Component<EffectGroupProps> {
  didSelectRow = (id: string) => {
    this.props.onSelect(id);
  };
  render() {
    return (
      <div className="effect-group">
        <h4>{this.props.title}</h4>
        <div>
          {this.props.items.map(item => {
            const id: string = item['id'] || item;
            const selected = id === this.props.selectedEffect;
            return (
              <EffectRow
                key={id}
                id={id}
                newLabel={item['label'] || null}
                selected={selected}
                isEditing={selected && this.props.isEditing}
                onClick={this.didSelectRow}
                onShowSettings={this.props.onShowSettings}
              />
            );
          })}
        </div>
      </div>
    );
  }
}

interface Props {
  layer: Layer;
  editableEffect: Effect | null;
  onSelect: (id: string) => void;
  onShowSettings: (
    target: HTMLElement,
    effect: Effect | CompositionEffect
  ) => void;
}

interface State {
  selectedEffect: string | null;
}

interface Item {
  id: string;
  label: string;
}

interface Group {
  title: string;
  items: (string | Item)[];
}

export default class extends React.Component<Props, State> {
  state = {
    selectedEffect: null
  };

  didSelectEffect = (effectId: string) => {
    this.setState({ selectedEffect: effectId });
    this.props.onSelect(effectId);
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.state.selectedEffect !== prevState.selectedEffect) {
      const el = ReactDOM.findDOMNode(this) as HTMLElement;
      setTimeout(() => {
        this.props.onShowSettings(
          el.querySelector('button.settings') as HTMLElement,
          getStoreState().flyer.previewedEffect!
        );
      }, 1);
    }
  }

  getEffectsGroups(hasText: boolean): Group[] {
    const layerHasTransparentFrame = this.props.layer.hasTransparentFrame();
    const compositions = {
      title: 'composition',
      items: Object.keys((window as any).BeatFlyer.compositions).filter(
        key => hasText || key !== 'composition-text'
      )
    };

    const featured = {
      title: 'featured',
      items: ['colorize', 'spherize', 'glow', 'blur-radial', 'rgb-split-radial']
    };
    if (layerHasTransparentFrame) {
      featured.items.push('persistence');
    }
    return [
      compositions,
      featured,
      {
        title: 'Element Geometry',
        items: [
          {
            id: 'geom-scale',
            label: 'Scale Element'
          },
          {
            id: 'geom-rotate',
            label: 'Rotate Element'
          },
          {
            id: 'geom-move',
            label: 'Move Element'
          }
        ]
      },
      {
        title: 'Basic',
        items: [
          'zoom',
          'move',
          'rotate',
          'opacity',
          'vibrate',
          'vignette',
          'drop-shadow'
        ]
      },
      {
        title: 'Cinema',
        items: ['light-leak', 'bokeh', 'bokeh-with-color']
      },
      {
        title: 'Color',
        items: [
          'hue',
          'monochrome',
          'dualtone',
          'halftone',
          'halftone-rgb',
          'color-invert',
          'desaturate',
          'color-saturate',
          'gradientize',
          'adjust-levels',
          'adjust-brightness-contrast'
        ]
      },
      {
        title: 'Blur',
        items: ['blur-kawase', 'blur-motion', 'pixelate']
      },
      {
        title: 'Distortion',
        items: ['stretch', 'tilt', 'swirl', 'distort', 'twist', 'wobble']
      },
      {
        title: 'Glitch',
        items: ['rgb-split', 'glitch-one', 'glitch-mosaic']
      },
      {
        title: 'Split',
        items: [
          'split',
          'split-accordion',
          'break-lines',
          'break-pieces',
          'break-slices'
        ]
      },
      {
        title: 'Mask',
        items: [
          'mask-from-center',
          'mask-from-side',
          'mask-from-the-middle',
          'mask-circular',
          'mask-arc'
        ]
      },
      {
        title: 'Texture',
        items: [
          'strobo',
          'clouds',
          'smoke',
          'concentrique',
          'stars',
          'waves',
          'rays',
          'radial-bands',
          'reflections',
          'snow'
        ]
      },
      {
        title: 'Others',
        items: [
          'outline',
          'liquify-from-side',
          'liquify-from-the-middle',
          'colored-edges'
        ]
      }
    ];
  }

  render() {
    let isEditing = false;
    if (this.state.selectedEffect && this.props.editableEffect) {
      const editableId = Object.getPrototypeOf(this.props.editableEffect)
        .constructor.id;
      isEditing = this.state.selectedEffect === editableId;
    }
    const hasText =
      this.props.layer.frames.filter(frame => frame.type === FrameType.Text)
        .length > 0;
    let groups = this.getEffectsGroups(hasText);
    /*const { effects } = this.props.layer;
    if (effects.length) {
      // filter out already applied effects
      let usedIds = effects.map(fx => (fx.constructor as any).id as string);
      if (this.state.selectedEffect) {
        // ignore selected effect
        usedIds = usedIds.filter(id => id !== this.state.selectedEffect);
      }
      for (const g of groups) {
        g.items = g.items.filter(item => !usedIds.includes(item));
      }
      groups = groups.filter(g => g.items.length > 0);
    }*/
    return (
      <div className="effect-picker">
        <PerfectScrollbar className="effects-groups">
          {groups.map((g: Group) => {
            return (
              <EffectGroup
                key={g.title}
                title={g.title}
                items={g.items}
                onSelect={this.didSelectEffect}
                selectedEffect={this.state.selectedEffect}
                isEditing={isEditing}
                onShowSettings={this.props.onShowSettings}
              />
            );
          })}
        </PerfectScrollbar>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/EffectPicker.tsx